import React, { useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { completeMicrosoftSignIn, microsoftErrorMessage } from "../../utils/microsoftSso";

/**
 * Where Microsoft sends the user back to (the redirect URI registered on the
 * Entra app). Finishes the sign-in, stores the Mobstr session, and moves on.
 */
export default function MicrosoftCallback({ complete = completeMicrosoftSignIn, go = (path) => window.location.replace(path) }) {
  const queryClient = useQueryClient();
  const [state, setState] = useState({ status: "working" });
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return; // React StrictMode runs effects twice in development; the code is single use
    ran.current = true;
    complete()
      .then((result) => {
        if (result.adminConsent) {
          setState({ status: "consented" });
          return;
        }
        localStorage.setItem("tokenmob", result.token);
        queryClient.invalidateQueries();
        go(result.returnTo && result.returnTo.startsWith("/") && !result.returnTo.startsWith("//") ? result.returnTo : "/dashboard");
      })
      .catch((e) => setState({ status: "failed", message: microsoftErrorMessage(e?.code || e?.response?.data?.error?.code) }));
  }, [complete, go, queryClient]);

  return (
    <div className="min-h-full flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="bg-white mt-8 py-8 px-6 shadow sm:rounded-lg sm:mx-auto sm:w-full sm:max-w-md text-center">
        {state.status === "working" && <p className="text-gray-700">Signing you in with Microsoft…</p>}
        {state.status === "consented" && (
          <>
            <h2 className="text-xl font-semibold text-gray-900">Consent granted</h2>
            <p className="mt-2 text-gray-600">People in your organisation can now sign in to Mobstr with Microsoft.</p>
            <a href="/settings" className="mt-4 inline-block text-mobstr-purple font-medium">Back to Settings</a>
          </>
        )}
        {state.status === "failed" && (
          <>
            <h2 className="text-xl font-semibold text-gray-900">Couldn't sign you in</h2>
            <p role="alert" className="mt-2 text-gray-600">{state.message}</p>
            <a href="/login" className="mt-4 inline-block text-mobstr-purple font-medium">Back to sign in</a>
          </>
        )}
      </div>
    </div>
  );
}
